import '../styles/v2/pages/onwatch.css';
import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import fetchWithTenant from '../utils/fetchWithTenant';
import { ArrowLeft, Loader2, Bell, Mail, Smartphone, Plus, Trash2 } from 'lucide-react';

const SEVERITY_CLASS = { CRITICAL: 'critical', WARNING: 'warning', INFO: 'informational' };

// One friendly label per condition type, plus (when the condition has one
// dominant numeric threshold) which conditionParams key it is and a unit
// label, so the page can offer a real "edit thresholds" control without
// needing twenty bespoke per-condition forms. Conditions with no primary
// threshold (e.g. collection stale, composite signatures) simply have no
// primaryParam and render no threshold field. group is display-only,
// for bucketing the Add rule condition picker.
const CONDITION_META = {
  ONWATCH_COLLECTION_STALE: { label: 'Collection stale', group: 'Cluster' },
  ONWATCH_WORKLOAD_NOT_READY: { label: 'Workload not ready', group: 'Cluster' },
  ONWATCH_RESTART_RATE: { label: 'Restart rate', primaryParam: 'restartsPerHour', unit: 'restarts/hour', default: 5, group: 'Cluster' },
  ONWATCH_POD_PENDING: { label: 'Pod pending', primaryParam: 'holdSeconds', unit: 'seconds', default: 900, group: 'Cluster' },
  ONWATCH_POD_FAILED: { label: 'Pod failed', group: 'Cluster' },
  ONWATCH_JOB_FAILED: { label: 'Job / CronJob failed', group: 'Cluster' },
  ONWATCH_NODE_CONDITION: { label: 'Node condition', group: 'Cluster' },
  ONWATCH_SERVICE_FAILED: { label: 'Service failed', group: 'Cluster' },
  ONWATCH_COMPOSITE_SIGNATURE: { label: 'Composite signature match', group: 'Cluster' },
  ONWATCH_DISK_LOW: { label: 'Disk low', primaryParam: 'usedPercentThreshold', unit: '% used', default: 80, group: 'Node resources' },
  ONWATCH_MEMORY_LOW: { label: 'Memory low', primaryParam: 'availablePercentThreshold', unit: '% available', default: 10, group: 'Node resources' },
  ONWATCH_LOAD_HIGH: { label: 'Load high', primaryParam: 'loadPerCoreThreshold', unit: 'load/core', default: 2, group: 'Node resources' },
  ONWATCH_GPU_UNAVAILABLE: { label: 'GPU unavailable', group: 'Node resources' },
  ONWATCH_CAMERA_DISCONNECTED: { label: 'Camera disconnected', group: 'Cameras and Points' },
  ONWATCH_CAMERA_MASS_DISCONNECT: { label: 'Camera mass disconnect', primaryParam: 'minCameras', unit: 'cameras', default: 3, group: 'Cameras and Points' },
  ONWATCH_CAMERA_FLAPPING: { label: 'Camera flapping', primaryParam: 'minTransitions', unit: 'transitions/hour', default: 6, group: 'Cameras and Points' },
  ONWATCH_CAMERA_NO_TRACKS: { label: 'Camera producing no tracks', group: 'Cameras and Points' },
  ONWATCH_CAMERA_FAILED_TO_RESUME: { label: 'Cameras failed to resume', group: 'Cameras and Points' },
  ONWATCH_POINT_OFFLINE: { label: 'Point offline', group: 'Cameras and Points' },
  ONWATCH_POINT_SYNC_STALE: { label: 'Point sync stale', primaryParam: 'intervalMultiplier', unit: 'x sync interval', default: 10, group: 'Cameras and Points' },
  ONWATCH_ACCESSORY_OFFLINE: { label: 'Accessory offline', group: 'Accessories and license' },
  ONWATCH_LICENSE_EXPIRING: { label: 'License expiring', primaryParam: 'thresholdDays', unit: 'days', default: 30, group: 'Accessories and license' },
  ONWATCH_QUOTA_EXHAUSTED: { label: 'Quota exhausted', primaryParam: 'thresholdPercent', unit: '% used', default: 90, group: 'Accessories and license' },
  ONWATCH_LATENCY_HIGH: { label: 'Latency elevated', primaryParam: 'deviationMultiplier', unit: 'x baseline', default: 3, group: 'Performance' },
};

const CONDITION_GROUP_ORDER = ['Cluster', 'Node resources', 'Cameras and Points', 'Accessories and license', 'Performance'];

export default function OnWatchAlertRules() {
  const { id } = useParams();

  const [instance, setInstance] = useState(null);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const [showAddForm, setShowAddForm] = useState(false);
  const [newConditionType, setNewConditionType] = useState('');
  const [newThreshold, setNewThreshold] = useState('');
  const [newSeverity, setNewSeverity] = useState('WARNING');
  const [newNotifyEmail, setNewNotifyEmail] = useState(false);
  const [newNotifyPush, setNewNotifyPush] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [instRes, rulesRes] = await Promise.all([
        fetchWithTenant(`/api/onwatch/instances/${id}`),
        fetchWithTenant(`/api/onwatch/instances/${id}/alert-rules`),
      ]);
      if (!instRes.ok) throw new Error(`HTTP ${instRes.status}`);
      if (!rulesRes.ok) throw new Error(`HTTP ${rulesRes.status}`);
      setInstance((await instRes.json()).instance);
      setRules((await rulesRes.json()).rules || []);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const patchRule = async (ruleId, patch) => {
    setSavingId(ruleId);
    try {
      const res = await fetchWithTenant(`/api/onwatch/instances/${id}/alert-rules/${ruleId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      if (res.ok) {
        const { rule } = await res.json();
        setRules((prev) => prev.map((r) => (r.id === ruleId ? rule : r)));
      }
    } finally {
      setSavingId(null);
    }
  };

  const createRule = async () => {
    if (!newConditionType) return;
    setCreating(true);
    setCreateError(null);
    try {
      const meta = CONDITION_META[newConditionType];
      const body = {
        conditionType: newConditionType,
        severity: newSeverity,
        notifyEmail: newNotifyEmail,
        notifyPush: newNotifyPush,
      };
      if (meta?.primaryParam && newThreshold !== '') {
        body.threshold = Number(newThreshold);
      }
      const res = await fetchWithTenant(`/api/onwatch/instances/${id}/alert-rules`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const payload = await res.json();
      if (!res.ok) {
        throw new Error(payload.message || payload.error || `HTTP ${res.status}`);
      }
      setRules((prev) => [...prev, payload.rule].sort((a, b) => a.name.localeCompare(b.name)));
      setShowAddForm(false);
      setNewConditionType('');
      setNewThreshold('');
      setNewSeverity('WARNING');
      setNewNotifyEmail(false);
      setNewNotifyPush(false);
    } catch (e) {
      setCreateError(e.message);
    } finally {
      setCreating(false);
    }
  };

  const deleteRule = async (rule) => {
    const conditionLabel = CONDITION_META[rule.conditionType]?.label || rule.conditionType;
    const message = rule.isSeeded
      ? `"${rule.name}" is a default rule. Deleting it stops ${conditionLabel} from being watched on this instance at all, not just silencing it - this cannot be undone without recreating the rule. Disabling it instead (the checkbox) is reversible in one click. Delete anyway?`
      : `Delete "${rule.name}"? This stops ${conditionLabel} from being watched on this instance.`;
    if (!window.confirm(message)) return;

    setDeletingId(rule.id);
    try {
      const res = await fetchWithTenant(`/api/onwatch/instances/${id}/alert-rules/${rule.id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirm: true }),
      });
      if (res.ok) {
        setRules((prev) => prev.filter((r) => r.id !== rule.id));
      } else {
        const payload = await res.json().catch(() => ({}));
        setError(payload.message || payload.error || `Failed to delete rule (HTTP ${res.status})`);
      }
    } finally {
      setDeletingId(null);
    }
  };

  if (loading && !instance) {
    return (
      <div className="page-content">
        <div className="md-loading"><Loader2 size={32} className="spin" /><p>Loading alert rules...</p></div>
      </div>
    );
  }

  if (error && !instance) {
    return (
      <div className="page-content">
        <div className="ow-form-error">{error}</div>
        <Link to={`/monitoring/onwatch/${id}`}><ArrowLeft size={14} /> Back to cluster health</Link>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Link to={`/monitoring/onwatch/${id}`} style={{ fontSize: 12, color: 'var(--text-tertiary)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <ArrowLeft size={12} /> {instance.name}
      </Link>

      <div className="page-header-with-actions">
        <div>
          <h1 className="page-title"><Bell size={24} /> Alert Rules</h1>
          <div className="ow-workload-detail-meta">
            {rules.length} rule{rules.length === 1 ? '' : 's'}. Most default rules ship with notification
            channels off, in-app only - turn on what you want notified.
          </div>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setShowAddForm((v) => !v)}>
          <Plus size={14} /> Add rule
        </button>
      </div>

      {showAddForm && (
        <div className="ow-accordion-section" style={{ padding: 16 }}>
          {createError && <div className="ow-form-error" style={{ marginBottom: 8 }}>{createError}</div>}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
              Condition
              <select
                className="form-input"
                value={newConditionType}
                onChange={(e) => { setNewConditionType(e.target.value); setNewThreshold(''); }}
              >
                <option value="">Select a condition...</option>
                {CONDITION_GROUP_ORDER.map((group) => (
                  <optgroup key={group} label={group}>
                    {Object.entries(CONDITION_META)
                      .filter(([, meta]) => meta.group === group)
                      .map(([type, meta]) => {
                        const hasExisting = rules.some((r) => r.conditionType === type);
                        return (
                          <option key={type} value={type}>
                            {meta.label}{hasExisting ? ' (already has a rule)' : ''}
                          </option>
                        );
                      })}
                  </optgroup>
                ))}
              </select>
            </label>

            {newConditionType && CONDITION_META[newConditionType]?.primaryParam ? (
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
                Threshold ({CONDITION_META[newConditionType].unit})
                <input
                  type="number"
                  className="form-input"
                  style={{ width: 110 }}
                  placeholder={String(CONDITION_META[newConditionType].default)}
                  value={newThreshold}
                  onChange={(e) => setNewThreshold(e.target.value)}
                />
              </label>
            ) : newConditionType ? (
              <div className="ow-form-help" style={{ maxWidth: 180 }}>
                This condition has no configurable threshold.
              </div>
            ) : null}

            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
              Severity
              <select className="form-input" value={newSeverity} onChange={(e) => setNewSeverity(e.target.value)}>
                <option value="INFO">INFO</option>
                <option value="WARNING">WARNING</option>
                <option value="CRITICAL">CRITICAL</option>
              </select>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
              <input type="checkbox" checked={newNotifyEmail} onChange={(e) => setNewNotifyEmail(e.target.checked)} />
              <Mail size={13} /> Email
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
              <input type="checkbox" checked={newNotifyPush} onChange={(e) => setNewNotifyPush(e.target.checked)} />
              <Smartphone size={13} /> Push
            </label>

            <button className="btn btn-primary btn-sm" disabled={!newConditionType || creating} onClick={createRule}>
              {creating ? 'Creating...' : 'Create rule'}
            </button>
            <button className="btn btn-sm" onClick={() => { setShowAddForm(false); setCreateError(null); }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="ow-accordion-section">
        <div className="ow-accordion-body">
          {rules.length === 0 && <div className="ow-form-help" style={{ padding: 16 }}>No rules yet.</div>}
          {rules.map((r) => {
            const meta = CONDITION_META[r.conditionType] || { label: r.conditionType };
            const paramValue = meta.primaryParam ? (r.conditionParams?.[meta.primaryParam] ?? meta.default) : null;
            return (
              <div key={r.id} className="ow-rule-row">
                <label className="ow-rule-toggle">
                  <input
                    type="checkbox"
                    checked={r.enabled}
                    disabled={savingId === r.id}
                    onChange={(e) => patchRule(r.id, { enabled: e.target.checked })}
                  />
                </label>
                <span className={`ow-badge ${SEVERITY_CLASS[r.severity] || 'unknown'}`}>{r.severity}</span>
                <span className="ow-workload-name" style={{ flex: 1, minWidth: 160 }}>
                  {r.name}
                  {r.isSeeded && <span className="ow-kind-badge" style={{ marginLeft: 6 }}>default</span>}
                </span>
                <span className="ow-form-help">{meta.label}</span>

                {meta.primaryParam && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <input
                      type="number"
                      className="form-input"
                      style={{ width: 70, padding: '2px 6px', fontSize: 12 }}
                      defaultValue={paramValue}
                      disabled={savingId === r.id}
                      onBlur={(e) => {
                        const v = Number(e.target.value);
                        if (Number.isFinite(v) && v !== paramValue) {
                          patchRule(r.id, { conditionParams: { ...(r.conditionParams || {}), [meta.primaryParam]: v } });
                        }
                      }}
                    />
                    <span className="ow-form-help">{meta.unit}</span>
                  </span>
                )}

                <span style={{ display: 'inline-flex', gap: 4 }} title="Notification channels">
                  <button
                    className={`btn btn-sm ${r.notifyEmail ? 'btn-secondary' : ''}`}
                    style={{ opacity: r.notifyEmail ? 1 : 0.4, padding: '4px 6px' }}
                    onClick={() => patchRule(r.id, { notifyEmail: !r.notifyEmail })}
                    title={r.notifyEmail ? 'Email on' : 'Email off'}
                  >
                    <Mail size={13} />
                  </button>
                  <button
                    className={`btn btn-sm ${r.notifyPush ? 'btn-secondary' : ''}`}
                    style={{ opacity: r.notifyPush ? 1 : 0.4, padding: '4px 6px' }}
                    onClick={() => patchRule(r.id, { notifyPush: !r.notifyPush })}
                    title={r.notifyPush ? 'Push on' : 'Push off'}
                  >
                    <Smartphone size={13} />
                  </button>
                </span>

                <button
                  className="btn btn-sm"
                  style={{ padding: '4px 6px', opacity: 0.6 }}
                  disabled={deletingId === r.id}
                  onClick={() => deleteRule(r)}
                  title={r.isSeeded ? 'Delete this default rule' : 'Delete this rule'}
                >
                  <Trash2 size={13} />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
